import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowSquareOut, Warning } from '@phosphor-icons/react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Current installed app version. Bump this on every release that needs the
// server-side `min_app_version` check to fire. Sourced from CRA env var so
// CI / release scripts can override at build-time without code edits.
export const APP_VERSION =
  process.env.REACT_APP_APP_VERSION || '0.1.0';

const VERSION_RE = /^\s*(\d+)\.(\d+)\.(\d+)\s*$/;

const parse = (v) => {
  if (!v) return [0, 0, 0];
  const m = VERSION_RE.exec(String(v));
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
};

// Returns true when `a < b`, comparing MAJOR.MINOR.PATCH lexicographically.
const isOlder = (a, b) => {
  const A = parse(a);
  const B = parse(b);
  for (let i = 0; i < 3; i++) {
    if (A[i] < B[i]) return true;
    if (A[i] > B[i]) return false;
  }
  return false;
};

const UpdateRequiredScreen = ({ minVersion, currentVersion, playStoreUrl, message }) => {
  const openStore = () => {
    if (playStoreUrl) window.open(playStoreUrl, '_blank', 'noopener,noreferrer');
  };
  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#FAF7F2] flex items-center justify-center px-6"
      data-testid="version-gate-blocker"
      role="alertdialog"
      aria-labelledby="version-gate-title"
      aria-describedby="version-gate-desc"
    >
      <div className="max-w-md w-full bg-white border border-[#E8E1D5] shadow-xl p-8 rounded-lg">
        <div className="flex justify-center mb-5">
          <div className="bg-brand-primary/10 p-4 rounded-full">
            <Warning size={44} weight="duotone" className="text-brand-primary" />
          </div>
        </div>
        <h1
          id="version-gate-title"
          className="font-heading text-2xl text-center text-text-primary mb-3"
          data-testid="version-gate-title"
        >
          Update Required
        </h1>
        <p
          id="version-gate-desc"
          className="text-center text-sm text-text-secondary mb-6 leading-relaxed"
          data-testid="version-gate-message"
        >
          {message ||
            'A newer version of BeeWeddy is available. Please update to continue — this version is no longer supported.'}
        </p>
        <div className="bg-sand-50 border border-[#E8E1D5] rounded p-3 mb-6 text-xs text-text-secondary flex items-center justify-between">
          <span>
            Your version:{' '}
            <span className="font-semibold text-text-primary" data-testid="version-gate-current">
              {currentVersion}
            </span>
          </span>
          <span>
            Required:{' '}
            <span className="font-semibold text-text-primary" data-testid="version-gate-required">
              {minVersion}
            </span>
          </span>
        </div>
        {playStoreUrl ? (
          <Button
            type="button"
            onClick={openStore}
            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none h-11 font-medium"
            data-testid="version-gate-store-btn"
          >
            Update on Play Store
            <ArrowSquareOut size={16} className="ml-2" />
          </Button>
        ) : (
          <Button
            type="button"
            disabled
            className="w-full rounded-none h-11 font-medium"
            data-testid="version-gate-store-btn-disabled"
          >
            Update Link Coming Soon
          </Button>
        )}
        <p className="text-[10px] text-center text-text-secondary mt-4">
          You won't lose any data. Just install the latest version and sign in again.
        </p>
      </div>
    </div>
  );
};

/**
 * VersionGate
 *
 * Wraps the application and blocks rendering of `children` if the installed
 * `APP_VERSION` is below the server-configured `min_app_version`. The check
 * runs once on mount; if the API call fails the gate is open (don't lock
 * users out on backend hiccups).
 */
const VersionGate = ({ children }) => {
  // 'idle' = checking, 'ok' = render children, 'blocked' = show update screen
  const [state, setState] = useState({ status: 'idle', cfg: null });

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/app-version`, {
          timeout: 8000,
        });
        if (cancelled) return;
        const min = data?.min_version || '0.0.0';
        if (isOlder(APP_VERSION, min)) {
          setState({ status: 'blocked', cfg: data });
        } else {
          setState({ status: 'ok', cfg: data });
        }
      } catch {
        // Fail-open: don't lock users out if the version endpoint errored.
        if (!cancelled) setState({ status: 'ok', cfg: null });
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'blocked') {
    const cfg = state.cfg || {};
    return (
      <UpdateRequiredScreen
        minVersion={cfg.min_version || '0.0.0'}
        currentVersion={APP_VERSION}
        playStoreUrl={cfg.play_store_url || ''}
        message={cfg.update_message || ''}
      />
    );
  }

  // 'idle' still renders children so users see something while the check runs.
  return children;
};

export default VersionGate;
